import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Bug, ChevronRight, CircleHelp, Loader2, MessagesSquare, Sparkles, type LucideIcon } from "lucide-react";
import {
  FOGBUGZ_FORMULATOR_REPO_NOT_SET_UP,
  askHandoffNavigationState,
  askVerdictLabel,
  fogbugzFormulatorKindForCategory,
  readAskHandoffState,
  type AskHandoff,
  type FogbugzFormulatorKind,
  type FogbugzFormulatorStartInput,
  type FogbugzFormulatorToolsetBridge,
} from "@ttc-spectree/toolbox-contract";
import { MarkdownRenderer } from "@/components/common/markdown-renderer";
import { Chip } from "@/components/grooming/grooming-chips";
import { RoutingScreen } from "@/components/fogbugz/routing-screen";
import { Attachments } from "@/components/toolbox/fogbugz-formulator/attachments";
import { InterviewPanel } from "@/components/toolbox/fogbugz-formulator/interview-panel";
import { ReviewStep } from "@/components/toolbox/fogbugz-formulator/review-step";
import { StopCard, stopFromStartError, type FogbugzFormulatorStop } from "@/components/toolbox/fogbugz-formulator/stop-card";
import { OpenInToolboxCallout } from "@/components/toolbox/open-in-toolbox-callout";
import { Button } from "@/components/ui/button";
import { useRoutingOptions, type RoutingSelection } from "@/hooks/queries/use-fogbugz";
import {
  useActiveToolboxInterviews,
  useStartToolboxInterview,
  useToolboxInterview,
  type ToolboxInterviewSession,
} from "@/hooks/queries/use-fogbugz-toolbox-interview";
import { canOn, projectsWith, useToolboxAccess } from "@/hooks/queries/use-toolbox-access";
import { usePageTitle } from "@/hooks/use-page-title";
import { ApiError } from "@/lib/api/client";
import { askVerdictTone } from "@/lib/toolbox/ask";
import { getApiErrorCode } from "@/lib/api/work-items";
import { useToolboxBridge, useToolsetSupport } from "@/lib/toolbox/bridge";
import {
  clearAcceptedSummaries,
  discardSession,
  discardedSessionIds,
  fogbugzFormulatorMessage,
  readAcceptedSummaries,
  useFogbugzFormulatorEvents,
  writeAcceptedSummaries,
} from "@/lib/toolbox/fogbugz-formulator";
import { FOGBUGZ_FORMULATOR_PATH, FOGBUGZ_FORMULATOR_TOOL_NAME, TOOLBOX_HOME_PATH } from "@/lib/toolbox/tools";
import { TOOLBOX_PAGE_TITLE } from "@/lib/toolbox/type-scale";
import { cn } from "@/lib/utils";
import { SUPPORT_LABELS } from "@/pages/toolbox/home";

/** The carried Product Q&A answer renders links as plain text, as Product Q&A does. */
const noHandoffLinks = () => false;

/**
 * `/toolbox/fogbugz-formulator`: the Toolbox FogBugz Formulator (epic
 * PI-SPEC-SW-E309, feature PI-SPEC-SW-1620; ER-1665 Package C). Someone files
 * a FogBugz case while their own Claude Code reads the product's code on
 * their computer and asks what is missing.
 *
 * - Without `?session=`: unfinished interviews to Continue or Discard, then
 *   the routing screen (FogBugz New Case order, no default project), then
 *   what is being filed (bug, feature or question; no default). Start creates
 *   the session on SpecTree, then hands it to the Toolbox
 *   (`fogbugzFormulator.start`). A FogBugz project with no ADO link, a linked
 *   ADO project with no repositories, or code not on this computer each stop
 *   on a plain card naming who can fix it; no interview runs.
 * - `?session=<id>`: the interview, read back from SpecTree, so a reload or
 *   an app restart resumes it.
 * - Opened from Product Q&A ("Start FogBugz Formulator", feature
 *   PI-SPEC-SW-1631): the hand-off arrives as navigation state
 *   (`readAskHandoffState`; an invalid one is ignored). A read-only "From
 *   Product Q&A" card sits above the routing screen, which still comes first
 *   with every field unchosen (`startUnchosen`: no default Category or
 *   Priority, and the product hint chooses nothing). The hand-off goes to the
 *   opening turn only, as `priorFindings` on `fogbugzFormulator.start`; it is
 *   never sent to SpecTree, and the person confirms every slot.
 *
 * Turns run on the person's Claude seat; SpecTree stores the interview text
 * only. Review and submit go page → SpecTree after the person reviews; Claude
 * never writes. Behind RequireFogbugzToolbox (the `fogbugz` capability on
 * some project and a saved FogBugz key). In a plain browser it shows how to
 * open SpecTree Toolbox; in an app without the fogbugz-formulator toolset,
 * the same update words as the home tile.
 */

/**
 * Sessions whose opening turn this window started. Kept in memory on
 * purpose: after a reload or restart the set is empty, so a session that
 * still awaits Claude has its turn run again here.
 */
const startedInThisWindow = new Set<string>();

/** The query parameter that names the open session. */
export const SESSION_PARAM = "session";

/** The page's path with a session open. Pure; exported for tests. */
export function fogbugzFormulatorSessionPath(sessionId: string): string {
  return `${FOGBUGZ_FORMULATOR_PATH}?${new URLSearchParams({ [SESSION_PARAM]: sessionId }).toString()}`;
}

function Waiting({ label, testId }: { label: string; testId?: string }) {
  return (
    <span className="flex items-center gap-2.5 text-[13px] text-muted-foreground" role="status" data-testid={testId}>
      <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />
      {label}
    </span>
  );
}

function PageFrame({ children, lead }: { children: ReactNode; lead?: string }) {
  return (
    <div className="flex w-full max-w-4xl flex-col gap-5 px-4 pb-6 pt-7">
      <div className="flex flex-col gap-1">
        <h1 className={TOOLBOX_PAGE_TITLE}>{FOGBUGZ_FORMULATOR_TOOL_NAME}</h1>
        {lead && <p className="text-[13px] text-muted-foreground">{lead}</p>}
      </div>
      {children}
    </div>
  );
}

function ErrorLine({ text, testId }: { text: string; testId: string }) {
  return (
    <div
      role="alert"
      className="rounded-lg border border-red-500/20 bg-red-500/10 p-3 text-sm text-red-400"
      data-testid={testId}
    >
      {text}
    </div>
  );
}

function errorText(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

// ---------------------------------------------------------------------------
// From Product Q&A
// ---------------------------------------------------------------------------

/** The bridge `start` input, with the Product Q&A hand-off when there is one. Pure; exported for tests. */
export function startInputFor(
  session: Pick<ToolboxInterviewSession, "id" | "adoProject" | "repos">,
  handoff: AskHandoff | null,
): FogbugzFormulatorStartInput {
  const input: FogbugzFormulatorStartInput = { sessionId: session.id, adoProject: session.adoProject, repos: session.repos };
  if (handoff) input.priorFindings = handoff;
  return input;
}

/** Plain words for where a hand-off question was asked: display only, never routing. */
function askedAbout(handoff: AskHandoff): string | null {
  const hint = handoff.productHint;
  if (hint.fogbugzProject) return hint.fogbugzArea ? `${hint.fogbugzProject} › ${hint.fogbugzArea}` : hint.fogbugzProject;
  return hint.adoProject ?? null;
}

function HandoffCard({ handoff }: { handoff: AskHandoff }) {
  const [open, setOpen] = useState(false);
  const product = askedAbout(handoff);
  return (
    <section
      className="glass-card flex flex-col gap-3 rounded-2xl border border-white/[0.08] p-5"
      aria-labelledby="fogbugz-formulator-handoff-title"
      data-testid="fogbugz-formulator-handoff"
    >
      <div className="flex flex-wrap items-center gap-2">
        <MessagesSquare className="h-4 w-4 text-primary" aria-hidden="true" />
        <h2 id="fogbugz-formulator-handoff-title" className="text-sm font-semibold">
          From Product Q&amp;A
        </h2>
        <Chip tone={askVerdictTone(handoff.verdict, handoff.specAgrees)}>{askVerdictLabel(handoff.verdict, handoff.specAgrees)}</Chip>
        {product && <span className="text-xs text-muted-foreground">Asked about {product}</span>}
      </div>
      <div className="flex flex-col gap-1">
        <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#A1A1AA]">You asked</span>
        <p className="whitespace-pre-wrap text-sm" data-testid="fogbugz-formulator-handoff-question">
          {handoff.question}
        </p>
      </div>
      <div className="flex flex-col gap-1">
        <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#A1A1AA]">The answer</span>
        <div
          className="max-h-48 overflow-y-auto text-[13px] text-muted-foreground"
          data-testid="fogbugz-formulator-handoff-answer"
        >
          <MarkdownRenderer
            content={handoff.answer}
            allowLink={noHandoffLinks}
            className="[&_ol]:list-outside [&_ol]:pl-[18px] [&_p]:mb-2 [&_ul]:list-outside [&_ul]:pl-[18px]"
          />
        </div>
      </div>
      {handoff.codeFindings.length > 0 && (
        <div>
          <button
            type="button"
            className="flex items-center gap-1.5 text-xs font-medium text-[#6CC4FF] hover:text-[#23A4FF]"
            aria-expanded={open}
            onClick={() => {
              setOpen((current) => !current);
            }}
            data-testid="fogbugz-formulator-handoff-code-toggle"
          >
            <ChevronRight className={cn("h-3.5 w-3.5 transition-transform", open && "rotate-90")} aria-hidden="true" />
            Where in the code ({handoff.codeFindings.length})
          </button>
          {open && (
            <ul className="mt-2 flex flex-col gap-1.5" data-testid="fogbugz-formulator-handoff-code">
              {handoff.codeFindings.map((finding, index) => (
                <li
                  key={`${finding.repo}-${finding.path}-${String(index)}`}
                  className="flex flex-col gap-0.5 rounded-md border border-[#1F1F23] bg-[#121215] px-2.5 py-1.5"
                >
                  <span className="break-all font-mono text-[11px] text-[#D4D4D8]">
                    {finding.repo} · {finding.path}
                    {finding.startLine !== undefined ? `:${String(finding.startLine)}` : ""}
                  </span>
                  <span className="text-xs text-muted-foreground">{finding.note}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      <p className="text-xs text-muted-foreground">
        Your Claude starts from this. You still choose where the case is filed, and you confirm every answer in your
        own words.
      </p>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Kind
// ---------------------------------------------------------------------------

const KIND_CHOICES: readonly { kind: FogbugzFormulatorKind; label: string; detail: string; icon: LucideIcon }[] = [
  { kind: "bug", label: "Something is broken", detail: "A bug: it should work, and it does not.", icon: Bug },
  {
    kind: "feature",
    label: "Something new or different",
    detail: "A feature: a new behaviour, or a change to how it works today.",
    icon: Sparkles,
  },
  { kind: "inquiry", label: "A question", detail: "An inquiry: you need an answer about how it works.", icon: CircleHelp },
];

/** Plain words for a kind, for the resume list. Pure; exported for tests. */
export function kindWords(kind: FogbugzFormulatorKind): string {
  return kind === "bug" ? "Bug" : kind === "feature" ? "Feature" : "Question";
}

function KindPicker({
  routing,
  kind,
  onKind,
  onBack,
  onStart,
  starting,
  error,
}: {
  routing: RoutingSelection;
  kind: FogbugzFormulatorKind | null;
  onKind: (kind: FogbugzFormulatorKind) => void;
  onBack: () => void;
  onStart: () => void;
  starting: boolean;
  error: string | null;
}) {
  return (
    <section className="glass-card space-y-4 rounded-2xl border border-white/[0.08] p-6" data-testid="fogbugz-formulator-kind">
      <div className="space-y-1">
        <h2 className="text-base font-semibold">What are you filing?</h2>
        <p className="text-sm text-muted-foreground" data-testid="fogbugz-formulator-routing-line">
          In {routing.sProject}
          {routing.sArea ? ` › ${routing.sArea}` : ""}. Claude asks different questions for each.
        </p>
      </div>
      <div role="radiogroup" aria-label="What are you filing?" className="grid gap-3 md:grid-cols-3">
        {KIND_CHOICES.map((choice) => {
          const Icon = choice.icon;
          const selected = kind === choice.kind;
          return (
            <button
              key={choice.kind}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => {
                onKind(choice.kind);
              }}
              className={cn(
                "flex flex-col items-start gap-1 rounded-lg border p-3 text-left transition-colors",
                selected ? "border-primary bg-primary/10" : "border-white/[0.08] hover:border-white/[0.2]",
              )}
              data-testid={`fogbugz-formulator-kind-${choice.kind}`}
            >
              <span className="flex items-center gap-2 text-sm font-medium">
                <Icon className="h-4 w-4 text-primary" aria-hidden="true" />
                {choice.label}
              </span>
              <span className="text-xs text-muted-foreground">{choice.detail}</span>
            </button>
          );
        })}
      </div>
      {error && <ErrorLine text={error} testId="fogbugz-formulator-start-error" />}
      <div className="flex items-center gap-3">
        <Button type="button" onClick={onStart} disabled={kind === null || starting}>
          {starting ? "Starting the interview…" : "Start interview"}
        </Button>
        <Button type="button" variant="outline" onClick={onBack} disabled={starting}>
          Back to routing
        </Button>
        {kind === null && <p className="text-xs text-muted-foreground">Choose one to begin.</p>}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Resume list
// ---------------------------------------------------------------------------

function ResumeList({
  sessions,
  onContinue,
  onDiscard,
}: {
  sessions: readonly ToolboxInterviewSession[];
  onContinue: (id: string) => void;
  onDiscard: (id: string) => void;
}) {
  if (sessions.length === 0) return null;
  return (
    <section className="flex flex-col gap-2" aria-labelledby="fogbugz-formulator-resume" data-testid="fogbugz-formulator-resume">
      <h2 id="fogbugz-formulator-resume" className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#A1A1AA]">
        Pick up where you left off
      </h2>
      <ul className="glass-card flex flex-col divide-y divide-[#18181B] rounded-[10px]">
        {sessions.map((session) => (
          <li key={session.id} className="flex items-center gap-3 px-4 py-3" data-testid="fogbugz-formulator-resume-row">
            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="truncate text-sm font-medium">
                {kindWords(session.kind)} in {session.routing.sProject}
                {session.routing.sArea ? ` › ${session.routing.sArea}` : ""}
              </span>
              <span className="truncate text-xs text-muted-foreground">
                {session.awaiting === "review"
                  ? "Ready to review"
                  : session.question ?? "Waiting for Claude's first question"}
              </span>
            </span>
            <Button
              type="button"
              size="sm"
              onClick={() => {
                onContinue(session.id);
              }}
            >
              Continue
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => {
                onDiscard(session.id);
              }}
            >
              Discard
            </Button>
          </li>
        ))}
      </ul>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Start: resume list, routing, kind, start (with the three stops)
// ---------------------------------------------------------------------------

function StartView({
  bridge,
  onOpen,
  handoff,
}: {
  bridge: FogbugzFormulatorToolsetBridge;
  onOpen: (id: string) => void;
  handoff: AskHandoff | null;
}) {
  const active = useActiveToolboxInterviews();
  const routingOptions = useRoutingOptions();
  const start = useStartToolboxInterview();
  const { data: access } = useToolboxAccess();
  const [step, setStep] = useState<"routing" | "kind">("routing");
  const [routing, setRouting] = useState<RoutingSelection | null>(null);
  const [kind, setKind] = useState<FogbugzFormulatorKind | null>(null);
  const [stop, setStop] = useState<FogbugzFormulatorStop | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [discarded, setDiscarded] = useState<string[]>(discardedSessionIds);

  const resumable = useMemo(
    // `submitting` too: a submit FogBugz never confirmed is resumed and
    // submitted again (SpecTree checks FogBugz for the case first).
    () => (active.data ?? []).filter((session) => session.status !== "filed" && !discarded.includes(session.id)),
    [active.data, discarded],
  );

  const discard = (id: string) => {
    discardSession(id);
    clearAcceptedSummaries(id);
    setDiscarded(discardedSessionIds());
    // The files kept on this computer go too; a failure leaves them for the app's own sweep.
    void bridge.forget({ sessionId: id }).catch(() => undefined);
  };

  /** Where a PM or Admin can fix a stop: the linked ADO project, else their first project with `map-project`. */
  const projectPageFor = (current: FogbugzFormulatorStop): string | undefined => {
    if (current.code !== "FOGBUGZ_PROJECT_NOT_LINKED") {
      return current.adoProject && canOn(access, current.adoProject, "map-project") ? current.adoProject : undefined;
    }
    return projectsWith(access, "map-project")[0];
  };

  const begin = async (chosenRouting: RoutingSelection, chosenKind: FogbugzFormulatorKind) => {
    if (starting) return;
    setStarting(true);
    setError(null);
    try {
      let session: ToolboxInterviewSession;
      try {
        // Routing exactly as chosen on the routing screen: nothing is added or inferred here.
        session = (await start.mutateAsync({ routing: chosenRouting, kind: chosenKind })).data;
      } catch (startError) {
        const refused = stopFromStartError(startError);
        if (refused) setStop(refused);
        else setError(errorText(startError, "The interview could not be started. Try again."));
        return;
      }
      let result: Awaited<ReturnType<FogbugzFormulatorToolsetBridge["start"]>>;
      try {
        result = await bridge.start(startInputFor(session, handoff));
      } catch {
        result = { ok: false, errorCode: "UNKNOWN", message: "" };
      }
      if (result.ok) {
        startedInThisWindow.add(session.id);
        onOpen(session.id);
        return;
      }
      if (result.errorCode === FOGBUGZ_FORMULATOR_REPO_NOT_SET_UP) {
        // No interview ran: hide the empty session here and drop its folder.
        discard(session.id);
        setStop({ code: FOGBUGZ_FORMULATOR_REPO_NOT_SET_UP, adoProject: session.adoProject });
        return;
      }
      setError(fogbugzFormulatorMessage(result.errorCode, { adoProject: session.adoProject, engineMessage: result.message }).text);
    } finally {
      setStarting(false);
    }
  };

  const backToRouting = () => {
    setStop(null);
    setError(null);
    setStep("routing");
  };

  return (
    <PageFrame lead="Report a bug, ask for a feature or ask a question in FogBugz. Your Claude reads the product code first, then asks what is missing. Nothing is filed until you review it.">
      {handoff && <HandoffCard handoff={handoff} />}

      {active.data && (
        <ResumeList
          sessions={resumable}
          onContinue={onOpen}
          onDiscard={discard}
        />
      )}

      {stop ? (
        <StopCard stop={stop} projectPageFor={projectPageFor(stop)} onBack={backToRouting} />
      ) : routingOptions.isLoading ? (
        <Waiting label="Loading your FogBugz projects" />
      ) : routingOptions.isError || !routingOptions.data ? (
        <ErrorLine
          text={errorText(routingOptions.error, "Your FogBugz projects could not be loaded. Try again.")}
          testId="fogbugz-formulator-routing-error"
        />
      ) : (
        <>
          {/* Kept mounted while choosing the kind, so Back keeps every routing choice. */}
          <div hidden={step !== "routing"}>
            <RoutingScreen
              options={routingOptions.data.data}
              submitLabel="Continue"
              startUnchosen={handoff !== null}
              isStarting={starting}
              onStart={(chosen) => {
                setRouting(chosen);
                setError(null);
                // A Bug, Feature or Inquiry category already says what is being
                // filed (as on the website): start at once. Only without one
                // does the person choose the kind.
                const categoryKind = fogbugzFormulatorKindForCategory(chosen.sCategory);
                if (categoryKind !== null) {
                  setKind(categoryKind);
                  void begin(chosen, categoryKind);
                  return;
                }
                setStep("kind");
              }}
            />
            {step === "routing" && error && <ErrorLine text={error} testId="fogbugz-formulator-start-error" />}
          </div>
          {step === "kind" && routing !== null && (
            <KindPicker
              routing={routing}
              kind={kind}
              onKind={setKind}
              onBack={backToRouting}
              onStart={() => {
                if (kind !== null) void begin(routing, kind);
              }}
              starting={starting}
              error={error}
            />
          )}
        </>
      )}
    </PageFrame>
  );
}

// ---------------------------------------------------------------------------
// One session
// ---------------------------------------------------------------------------

function SessionView({
  bridge,
  sessionId,
  handoff,
}: {
  bridge: FogbugzFormulatorToolsetBridge;
  sessionId: string;
  handoff: AskHandoff | null;
}) {
  const navigate = useNavigate();
  const query = useToolboxInterview(sessionId);
  const events = useFogbugzFormulatorEvents(bridge, sessionId);
  const { markRunning, markFailed, clearFailure } = events;
  const [pendingAnswer, setPendingAnswer] = useState<string | null>(null);
  const [keptAnswer, setKeptAnswer] = useState<string | null>(null);
  const [stop, setStop] = useState<FogbugzFormulatorStop | null>(null);
  const [phase, setPhase] = useState<"interview" | "review">("interview");
  // Reviewed event-log summaries stay on this computer with the files, so a reload or restart keeps them.
  const [summaries, setSummaries] = useState<Record<string, string>>(() => readAcceptedSummaries(sessionId));
  const changeSummaries = useCallback(
    (next: Record<string, string>) => {
      writeAcceptedSummaries(sessionId, next);
      setSummaries(next);
    },
    [sessionId],
  );
  const turnsAtSend = useRef<number | null>(null);
  const resumeTried = useRef(false);
  const session = query.data;

  const fail = useCallback(
    (code: string, message: string) => {
      markFailed(code, message);
    },
    [markFailed],
  );

  /** Run the turn Claude owes (the opening turn, or one cut short by a restart) on this computer. */
  const runOwedTurn = useCallback(
    (current: ToolboxInterviewSession) => {
      markRunning("opening");
      startedInThisWindow.add(current.id);
      // The Product Q&A hand-off belongs to the opening turn only.
      bridge
        .start(startInputFor(current, current.turns === 0 ? handoff : null))
        .then((result) => {
          if (result.ok) return;
          if (result.errorCode === FOGBUGZ_FORMULATOR_REPO_NOT_SET_UP) {
            // No turn runs: stop the progress line without a turn-failed notice, and show the stop card.
            markFailed(result.errorCode, result.message);
            clearFailure();
            setStop({ code: FOGBUGZ_FORMULATOR_REPO_NOT_SET_UP, adoProject: current.adoProject });
            return;
          }
          fail(result.errorCode, result.message);
        })
        .catch(() => {
          fail("UNKNOWN", "");
        });
    },
    [bridge, clearFailure, fail, handoff, markFailed, markRunning],
  );

  // A session that awaits Claude: started here a moment ago (its turn runs), or
  // reopened (reload, restart, resume list), where nothing runs yet.
  useEffect(() => {
    if (!session || resumeTried.current) return;
    if (session.status !== "active" || session.awaiting !== "claude") return;
    resumeTried.current = true;
    if (startedInThisWindow.has(session.id)) markRunning("opening");
    else runOwedTurn(session);
  }, [markRunning, runOwedTurn, session]);

  // SpecTree has the turn: the opening turn moved the session off "claude", or a reply added a turn.
  const turns = session?.turns;
  const awaiting = session?.awaiting;
  useEffect(() => {
    if (turns === undefined) return;
    if (turnsAtSend.current !== null && turns > turnsAtSend.current) {
      turnsAtSend.current = null;
      setPendingAnswer(null);
      setKeptAnswer(null);
    }
  }, [turns]);

  const failure = events.turn.failure;
  // A failed reply keeps the answer: back into the box, never lost.
  useEffect(() => {
    if (failure && pendingAnswer !== null) {
      setKeptAnswer(pendingAnswer);
      setPendingAnswer(null);
      turnsAtSend.current = null;
    }
  }, [failure, pendingAnswer]);

  const sendAnswer = (answer: string) => {
    if (!session) return;
    setKeptAnswer(null);
    setPendingAnswer(answer);
    turnsAtSend.current = session.turns;
    markRunning("reply");
    bridge
      .answer({ sessionId: session.id, answer })
      .then((result) => {
        if (!result.ok) fail(result.errorCode, result.message);
      })
      .catch(() => {
        fail("UNKNOWN", "");
      });
  };

  if (query.isPending) {
    return (
      <PageFrame>
        <Waiting label="Opening the interview" />
      </PageFrame>
    );
  }

  if (query.isError || !session) {
    const code = getApiErrorCode(query.error);
    const details = query.error instanceof ApiError ? (query.error.data as { error?: { details?: { code?: unknown } } } | undefined) : undefined;
    const gone = code === "NOT_FOUND" || details?.error?.details?.code === "INTERVIEW_SESSION_NOT_FOUND";
    return (
      <PageFrame>
        <ErrorLine
          text={gone ? "This interview has ended or expired. Start a new one." : errorText(query.error, "The interview could not be loaded.")}
          testId="fogbugz-formulator-session-error"
        />
        <Link to={FOGBUGZ_FORMULATOR_PATH} className="text-[13px] text-link hover:underline">
          Start a new one
        </Link>
      </PageFrame>
    );
  }

  if (session.status === "filed") {
    return (
      <PageFrame>
        <div className="rounded-lg border border-green-500/20 bg-green-500/10 p-4 text-sm" role="status" data-testid="fogbugz-formulator-filed">
          {session.filedCaseId !== null
            ? `This report was filed as FogBugz case #${String(session.filedCaseId)}.`
            : "This report was filed in FogBugz."}
        </div>
        <Link to={FOGBUGZ_FORMULATOR_PATH} className="text-[13px] text-link hover:underline">
          File another
        </Link>
      </PageFrame>
    );
  }

  const routingLine = `${kindWords(session.kind)} in ${session.routing.sProject}${session.routing.sArea ? ` › ${session.routing.sArea}` : ""} · code from ${session.adoProject}`;

  if (stop) {
    return (
      <PageFrame lead={routingLine}>
        <StopCard
          stop={stop}
          onBack={() => {
            void navigate(FOGBUGZ_FORMULATOR_PATH);
          }}
        >
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              setStop(null);
              runOwedTurn(session);
            }}
          >
            Try again
          </Button>
        </StopCard>
      </PageFrame>
    );
  }

  // The opening turn is done once the session leaves "claude"; a reply once SpecTree has its turn.
  const running =
    events.turn.running && (events.turn.running.mode === "reply" || awaiting === "claude") ? events.turn.running : null;

  return (
    <PageFrame lead={routingLine}>
      {phase === "interview" && (
        <InterviewPanel
          session={session}
          running={running}
          pendingAnswer={pendingAnswer}
          keptAnswer={keptAnswer}
          failure={failure ? fogbugzFormulatorMessage(failure.code, { adoProject: session.adoProject, engineMessage: failure.message }) : null}
          onRetry={() => {
            if (keptAnswer !== null) sendAnswer(keptAnswer);
            else runOwedTurn(session);
          }}
          onAnswer={sendAnswer}
          onReview={() => {
            setPhase("review");
          }}
          attachments={
            <Attachments
              bridge={bridge}
              sessionId={session.id}
              adoProject={session.adoProject}
              summaries={summaries}
              onSummariesChange={changeSummaries}
            />
          }
        />
      )}
      {phase === "review" && (
        <ReviewStep
          bridge={bridge}
          session={session}
          summaries={summaries}
          attachments={
            <Attachments
              bridge={bridge}
              sessionId={session.id}
              adoProject={session.adoProject}
              summaries={summaries}
              onSummariesChange={changeSummaries}
              readOnly
            />
          }
          onBack={() => {
            setPhase("interview");
          }}
        />
      )}
    </PageFrame>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function FogbugzFormulatorFlow({ bridge }: { bridge: FogbugzFormulatorToolsetBridge }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Navigation state only (never the URL, never SpecTree); an invalid hand-off is ignored.
  const handoff = useMemo(() => readAskHandoffState(location.state), [location.state]);
  const sessionId = params.get(SESSION_PARAM)?.trim() ?? "";
  const open = useCallback(
    (id: string) => {
      // Kept with the session page, so a retried opening turn still gets the hand-off.
      void navigate(fogbugzFormulatorSessionPath(id), handoff ? { state: askHandoffNavigationState(handoff) } : undefined);
    },
    [handoff, navigate],
  );
  if (sessionId !== "") return <SessionView key={sessionId} bridge={bridge} sessionId={sessionId} handoff={handoff} />;
  return <StartView bridge={bridge} onOpen={open} handoff={handoff} />;
}

/** `/toolbox/fogbugz-formulator`. */
export function ToolboxFogbugzFormulatorPage() {
  usePageTitle(FOGBUGZ_FORMULATOR_TOOL_NAME);
  const bridge = useToolboxBridge();
  const support = useToolsetSupport("fogbugz-formulator");
  const formulator = bridge?.fogbugzFormulator ?? null;

  if (!bridge) {
    return (
      <div className="container mx-auto max-w-3xl px-4 py-6">
        <OpenInToolboxCallout reason="The FogBugz Formulator runs in SpecTree Toolbox: your own Claude Code reads the product code on your computer to ask better questions." />
      </div>
    );
  }

  if (support === undefined) {
    return (
      <div className="flex w-full px-4 pt-7">
        <Waiting label="Checking SpecTree Toolbox" />
      </div>
    );
  }

  if (support !== "supported" || !formulator) {
    const label = SUPPORT_LABELS[support === "spectree-update-needed" ? "spectree-update-needed" : "app-update-needed"];
    return (
      <div className="flex w-full flex-col gap-3 px-4 pb-6 pt-7" data-testid="fogbugz-formulator-update-needed">
        <h1 className={TOOLBOX_PAGE_TITLE}>{FOGBUGZ_FORMULATOR_TOOL_NAME}</h1>
        <div>
          <Chip tone={label.tone}>{label.text}</Chip>
        </div>
        <Link to={TOOLBOX_HOME_PATH} className="text-[13px] text-link hover:underline">
          Back to SpecTree Toolbox
        </Link>
      </div>
    );
  }

  return <FogbugzFormulatorFlow bridge={formulator} />;
}
